import { FormEvent, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { taskBoardService, type Carteira } from '@/services/task-board-service'
import { apiErrorMessage, type TeamMember } from '@/services/team-service'
import * as toast from '@/lib/toast'

export interface CarteirasPanelProps {
  carteiras: Carteira[]
  members: TeamMember[]
  canEdit: boolean
  onChanged: () => void
}

/** Carteiras = grupos de pessoas usados como filtro (ex.: "Paulo + Bruno"). Só sócio altera. */
export function CarteirasPanel({ carteiras, members, canEdit, onChanged }: CarteirasPanelProps) {
  const [nome, setNome] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))

  const create = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await taskBoardService.createCarteira({ nome: nome.trim(), membros: selected })
      setNome('')
      setSelected([])
      toast.success('Carteira criada')
      onChanged()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Não foi possível criar a carteira'))
    } finally {
      setSaving(false)
    }
  }

  const deactivate = async (carteira: Carteira) => {
    try {
      await taskBoardService.updateCarteira(carteira.id, { ativo: false })
      toast.success(`Carteira "${carteira.nome}" desativada`)
      onChanged()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Não foi possível desativar'))
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-2">
        {carteiras.length === 0 && <li className="text-sm text-muted-foreground">Nenhuma carteira cadastrada.</li>}
        {carteiras.map((c) => (
          <li key={c.id} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
            <span>
              <span className="font-medium">{c.nome}</span>
              <span className="text-muted-foreground"> · {c.membros.map((m) => m.nome).join(', ')}</span>
            </span>
            {canEdit && <Button size="sm" variant="ghost" onClick={() => deactivate(c)}>Desativar</Button>}
          </li>
        ))}
      </ul>
      {canEdit && (
        <form className="flex flex-col gap-2 border-t pt-3" onSubmit={create}>
          <Input aria-label="Nome da carteira" placeholder="Ex.: Paulo + Bruno" value={nome} onChange={(e) => setNome(e.target.value)} />
          <div className="flex flex-wrap gap-3">
            {members.map((m) => (
              <label key={m.id} className="flex items-center gap-1.5 text-sm">
                <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggle(m.id)} />
                {m.fullName || m.email}
              </label>
            ))}
          </div>
          <Button type="submit" size="sm" className="self-end" disabled={saving || !nome.trim() || selected.length === 0}>
            Criar carteira
          </Button>
        </form>
      )}
    </div>
  )
}
